<template>
  <div class="create-case-modal">
    <b-modal
      :id="idModal"
      ref="bModal"
      size="full"
      modal-class="create-case-modal"
      centered
      qa="create-case-modal"
      @ok="onOk"
      @hide="onHide"
      @shown="focusIdentifier"
    >
      <template #modal-title>
        <i
          class="fal fa-chart-network mr-2"
          aria-hidden="true"
        />
        {{ isReassign ? $t('registry.btn.reassignPatient') : $t('component.patient.searcher.createCase') }}
      </template>
      <b-container
        fluid
        class="p-2"
      >
        <b-row
          v-if="!isReassign"
          class="d-flex justify-content-between"
        >
          <b-col>
            <span class="dark">{{ $t('component.patient.searcher.caseDescription') }}:</span>
          </b-col>
        </b-row>
        <div
          class="search-box float-component px-3 my-3"
          :class="{'search-done': searchDone}"
        >
          <b-form>
            <b-row>
              <b-col
                class="px-1 d-flex align-items-center"
                cols="1"
              >
                <div class="select-section identifiers">
                  <vt-combo-select
                    v-model="identifierSelected"
                    qa="create-case-modal-identifier-div"
                    class="nowrap id-model-select"
                    track-by="code"
                    :options="identifiers"
                    label="name"
                  />
                </div>
              </b-col>
              <b-col
                v-if="identifierSelected && identifierSelected.code === centerIdentifierCode"
                class="px-1 d-flex align-items-center"
              >
                <div class="select-section">
                  <vt-combo-select
                    v-model="centerSelected"
                    qa="create-case-modal-center-div"
                    class="center-selection"
                    track-by="code"
                    :options="centers"
                    label="name"
                    :placeholder="$t('component.patient.searcher.center')"
                  />
                </div>
              </b-col>
              <b-col class="px-1">
                <div class="input-section">
                  <vt-input
                    ref="patientIdentifier"
                    v-model="id"
                    qa="create-case-modal-identifier-input"
                    :placeholder="$tc('global.identifier', 1)"
                    :name="$tc('global.identifier', 1)"
                    class="form-control-sm"
                    type="text"
                    autofocus
                  />
                </div>
              </b-col>
              <b-col class="px-1">
                <div class="input-section">
                  <vt-input
                    v-model="surname1"
                    qa="create-case-modal-surname1-input"
                    :placeholder="$t('global.surname1')"
                    :name="$t('global.surname1')"
                    class="form-control-sm"
                    type="text"
                  />
                </div>
              </b-col>
              <b-col
                v-if="globalParameters.secondSurnameSupported"
                class="px-1"
              >
                <div class="input-section">
                  <vt-input
                    v-model="surname2"
                    qa="create-case-modal-surname2-input"
                    :placeholder="$t('global.surname2')"
                    :name="$t('global.surname2')"
                    class="form-control-sm"
                    type="text"
                  />
                </div>
              </b-col>
              <b-col class="px-1">
                <div class="input-section">
                  <vt-input
                    v-model="name"
                    qa="create-case-modal-name-input"
                    :placeholder="$tc('global.name', 1)"
                    :name="$tc('global.name', 1)"
                    class="form-control-sm"
                    type="text"
                  />
                </div>
              </b-col>
              <b-col
                v-if="globalParameters.middleNameSupported"
                class="px-1"
              >
                <div class="input-section">
                  <vt-input
                    v-model="middleName"
                    qa="create-case-modal-middlename-input"
                    :placeholder="$t('global.middleName')"
                    :name="$t('global.middleName')"
                    class="form-control-sm"
                    type="text"
                  />
                </div>
              </b-col>
              <b-col
                class="ttext-center icon-column d-flex align-items-center justify-content-end px-1"
                cols="auto"
              >
                <div class="d-flex align-items-center justify-content-end">
                  <b-button
                    v-if="searchDone"
                    qa="create-case-modal-clear-button"
                    class="btn__icon btn__icon--primary  alt-btn px-1"
                    @click="clearSearch"
                  >
                    <i
                      class="far fa-times"
                      aria-hidden="true"
                    />
                  </b-button>
                  <vt-loading-button
                    qa="create-case-modal-search-button"
                    class="btn__icon btn__icon--primary px-2"
                    :params="{ promise: searchPatients, shortkeys: createCaseModalSearchButtonShortkeys }"
                  >
                    <i
                      class="fas fa-search"
                      aria-hidden="true"
                    />
                  </vt-loading-button>
                </div>
              </b-col>
            </b-row>
          </b-form>
        </div>
      </b-container>
      <div>
        <hr class="m-0">
      </div>
      <div
        v-if="loading"
        class="loading-state"
      >
        <vt-loading />
      </div>
      <div
        v-if="!loading"
        class="patient-container justify-content-center"
      >
        <div
          v-if="noSearchDoneYet || tooManyResults || !rowData.length"
          class="empty-search d-flex align-items-center justify-content-center p-4"
        >
          <div class="w-100">
            <b-row>
              <b-col>
                <div class="d-flex flex-column text-center">
                  <template v-if="noSearchDoneYet">
                    <div>
                      <i
                        class="fal fa-th-list fa-2x mb-2"
                        aria-hidden="true"
                      />
                      <i
                        class="far fa-search ml-1"
                        aria-hidden="true"
                      />
                    </div>
                    <span class="mt-3">
                      {{ $t('component.patient.searcher.emptyStateText') }}
                    </span>
                    <span
                      v-if="!isReassign"
                    >
                      {{ $t('component.patient.searcher.emptyStateTextCase') }}
                    </span>
                  </template>
                  <template v-else>
                    <div>
                      <i
                        class="error fal fa-exclamation-circle fa-2x mb-2"
                        aria-hidden="true"
                      />
                    </div>
                    <span class="error">
                      {{ tooManyResults ? $t('component.patient.searcher.tooManySearchResults') : $t('component.patient.searcher.noResults') }}
                    </span>
                  </template>
                </div>
              </b-col>
            </b-row>
            <b-row
              v-if="!isReassign && $_canCreatePatient()"
              class="d-flex justify-content-center mt-5"
            >
              <b-col cols="auto">
                <b-button
                  qa="create-case-modal-create-patient-button"
                  block
                  class="btn__outline"
                  @click="showCreatePatientModal"
                >
                  <i
                    class="far fa-user-plus mr-2"
                    aria-hidden="true"
                  />
                  {{ $t('component.patient.searcher.createCaseToNewPatient') }}
                </b-button>
              </b-col>
            </b-row>
          </div>
        </div>
        <div>
          <vt-grid
            v-if="rowData.length > 0"
            qa="create-case-modal-patients-grid-div"
            class="ps-grid patient mt-3"
            :grid-options="gridOptions"
            :column-defs="columnDefs"
            :row-data="rowData"
            master-detail
            use-detail-custom-config
            :detail-cell-renderer-params="detailCellRendererParams"
            pagination
            :autosize-columns="false"
            component-code="uicomponent.petitioner.patientgrid"
          />
        </div>
      </div>
      <div
        slot="modal-footer"
        class="footer-banner pr-2 pl-2 w-100"
      >
        <div class="row">
          <div class="col text-left">
            <b-button
              qa="create-case-modal-cancel-button"
              size="sm"
              variant="outline-secondary"
              data-dismiss="modal"
              @click="hide"
            >
              {{ $t('global.cancel') }}
            </b-button>
          </div>
          <div
            v-if="rowData.length > 0"
            class="col-10 text-right"
          >
            <b-button
              v-if="!isReassign"
              qa="create-case-modal-create-patient-button"
              size="sm"
              class="btn__outline mr-2"
              @click="showCreatePatientModal"
            >
              <i
                class="far fa-user-plus mr-2"
                aria-hidden="true"
              />
              {{ $t('component.patient.searcher.createCaseToNewPatient') }}
            </b-button>
            <b-button
              v-if="!isReassign"
              qa="create-case-modal-case-wizard-button"
              size="sm"
              class="btn__outline mr-2"
              :disabled="selectedRows.length !== 1"
              @click="onCreateCaseWithWizardClick"
            >
              <i
                class="far fa-window-restore mr-2"
                aria-hidden="true"
              />
              {{ $t('component.patient.searcher.createCaseWithWizard') }}
            </b-button>
            <b-button
              :qa="`create-case-modal-${isReassign ? 'reassign-patient' : 'create-case-to-patient'}-button`"
              size="sm"
              class="btn--primary"
              :disabled="selectedRows.length !== 1"
              @click="selectPatientAlt(selectedRows[0])"
            >
              <i
                class="far fa-chart-network mr-2"
                aria-hidden="true"
              />
              {{ isReassign ? $t('registry.btn.reassignPatient') : $t('component.patient.searcher.createCaseToPatient') }}
            </b-button>
          </div>
        </div>
      </div>
    </b-modal>
    <modal-patient-card
      ref="modalPatientCard"
      :patient-id="patientIdSelected"
    />
    <create-patient-modal
      id="createPatientModal"
      ref="createPatientModal"
      :create-case="true"
      @hide="onCreatePatientModalHide"
      @createCaseInNewCaseRoute="onCreateCaseInNewCaseRoute"
    />
    <modal-wrapper ref="modalAssociateCaseWrapper">
      <modal-associated-cases-read-only
        ref="modalAssociateCase"
        :associated-cases="selectedCaseAssociatedCases"
        :patient-id="selectedCasePatientId"
        :case-number="selectedCaseCaseNumber"
      />
    </modal-wrapper>
    <modal-wrapper ref="registryWizard">
      <registry-wizard
        @hide="onRegistryWizardHide"
        @createCase="onRegistryWizardCreateCase"
      />
    </modal-wrapper>
    <modal-wrapper ref="patientSelectionModalWrapper">
      <patient-selection-modal
        id="patientSelectionModal"
        continue-with-case-creation
        @updateLocalPatientAndContinueWithCaseCreation="onPatientSelectionModalContinue($event)"
        @continue="onPatientSelectionModalContinue($event)"
      />
    </modal-wrapper>
  </div>
</template>
<script>
import CreatePatientModal from '@/components/patient/CreatePatientModal'
import sessionStore from '@/session/sessionStore'
import gridSideBarConfig from '@/grid/gridSideBarConfig'
import VtGrid from '@/components/VtGrid/VtGrid'
import patientResource from '@/api/resources/PatientResource'
import apiExceptionType from '@/utils/apiExceptionType'
import { REQUEST_STATES } from '@/api/resources/entityState/request-state'
import caseServices from '@/api/resources/common/commonCaseServices'
import buildIconHeaderTemplate from '@/grid/buildIconHeaderTemplate'
import ModalAssociatedCasesReadOnly from '@/components/ModalAssociatedCasesReadOnly'
import RegistryWizard from '@/modules/RegistryModule/components/registryWizard/RegistryWizard'
import PatientIdentifierResource from '@/api/resources/PatientIdentifierResource'
import CenterResource from '@/api/resources/common/commonCenterService'
import i18n from '@/lang/i18n'
import ModalPatientCard from '@/components/modals/ModalPatientCard'
import { PatientOperationGuardMixin } from '@/api/operations/mixins/guards/patient-operation-guard.mixin'
import { PAGINATOR_BIG_PAGE_SIZE } from '@/shared/services/services-constants'
import patientDiscrepancyServices from '@/api/resources/patientDiscrepancyServices'
import PatientSelectionModal from '@/modules/RegistryModule/components/patientDiscrepancyModal/PatientSelectionModal'
import { AgGridColumnType, AgGridRowModelType } from '@/shared/models/ag-grid.model'
import CellGridRichText from '@/components/common/CellGridRichText'
import { StoreUtils } from '@/shared/util/store-utils'
import { HelperUtils } from '@/shared/util/helper-utils'

const ROW_HEIGHT_NORMAL = 30
const ROW_HEIGHT_NO_DATA = 116

export default {
    name: 'CreateCaseModal',
    components: {
        VtGrid,
        CreatePatientModal,
        ModalAssociatedCasesReadOnly,
        ModalPatientCard,
        RegistryWizard,
        PatientSelectionModal
    },
    mixins: [PatientOperationGuardMixin],
    props: {
        idModal: { type: String, required: true }
    },
    data() {
        return {
            isReassign: false,
            patientIdSelected: null,
            identifiers: [],
            centers: [],
            centerIdentifierCode: 'identifierType.NHC',
            centerSelected: null,
            identifierSelected: null,
            isModalOpen: false,
            selectedRows: [],
            id: null,
            surname1: null,
            surname2: null,
            middleName: null,
            name: null,
            tooManyResults: false,
            loading: false,
            noSearchDoneYet: true,
            selectedPatient: null,
            selectedCaseId: null,
            selectedStudyRequest: null,
            gridApi: null,
            rowData: [],
            coreEntity: 'PATIENT',
            detailCellRendererParams: {},
            selectedCaseAssociatedCases: [],
            selectedCaseCaseNumber: null,
            selectedCasePatientId: null,
            selected: null,
            selectedIdentifiers: null,
            searchDone: false,
            columnDefs: [
                { type: AgGridColumnType.MASTER_DETAIL },
                { type: AgGridColumnType.CUSTOM_CHECKBOX },
                {
                    type: AgGridColumnType.DIACRITICS_INSENSITIVE_TEXT_FOR_CODE_DESCRIPTION_OR_STRING,
                    field: 'patName',
                    flex: 1,
                    minWidth: 120,
                    headerName: i18n.tc('global.name')
                },
                {
                    type: AgGridColumnType.DIACRITICS_INSENSITIVE_TEXT_FOR_CODE_DESCRIPTION_OR_STRING,
                    field: 'patMiddleName',
                    flex: 1,
                    minWidth: 120,
                    headerName: i18n.t('grid.patMiddleName')
                },
                {
                    type: AgGridColumnType.DIACRITICS_INSENSITIVE_TEXT_FOR_CODE_DESCRIPTION_OR_STRING,
                    field: 'patSurname1',
                    minWidth: 150,
                    headerName: i18n.t('global.surname1')
                },
                {
                    type: AgGridColumnType.DIACRITICS_INSENSITIVE_TEXT_FOR_CODE_DESCRIPTION_OR_STRING,
                    field: 'patSurname2',
                    minWidth: 150,
                    headerName: i18n.t('global.surname2')
                },
                {
                    type: AgGridColumnType.DIACRITICS_INSENSITIVE_TEXT_FOR_CODE_DESCRIPTION_OR_STRING,
                    field: 'genderName',
                    minWidth: 120,
                    headerName: i18n.tc('global.gender')
                },
                {
                    type: AgGridColumnType.DATE,
                    field: 'patBirthdate',
                    colId: 'patBirthdate',
                    headerName: i18n.t('global.birthday'),
                    minWidth: 120,
                    flex: 1
                },
                // TODO: la columna HIS está mockeada, se debe enlazar, no está traducido porque entiendo que es dinámico
                {
                    type: 'his',
                    headerName: i18n.t('HIS'),
                    minWidth: 80,
                    flex: 1
                },
                {
                    type: 'text',
                    field: 'centerIdentifier',
                    colId: 'centerIdentifier',
                    minWidth: 120,
                    headerName: 'NHC'
                }
            ],
            createCaseModalSearchButtonShortkeys: ['enter']
        }
    },
    computed: {
        sessionStoreState() {
            return sessionStore.state
        },
        laboratoryId() {
            return this.$store.getters.labId
        },
        gridOptions() {
            return {
                onRowDoubleClicked: this.onRowDoubleClicked,
                pagination: false,
                rowModelType: AgGridRowModelType.CLIENT_SIDE,
                getContextMenuItems: this.getContextMenuItems,
                floatingFilter: false,
                sideBar: gridSideBarConfig,
                onGridReady: this.onGridReady,
                onRowDataChanged: this.onRowDataChanged,
                onSelectionChanged: this.onSelectionChanged
            }
        },
        regex() {
            const identifierCodeSelected = this.identifierSelected && this.identifierSelected.code
            return this.$store.getters.regex.filter((regex) => identifierCodeSelected === regex.identifierType.code)
        }
    },
    methods: {
        cleanCreateCaseModalShorteys() {
            this.createCaseModalSearchButtonShortkeys = []
        },
        setCreateCaseModalShorteys() {
            this.createCaseModalSearchButtonShortkeys = ['enter']
        },
        onCreatePatientModalHide() {
            this.setCreateCaseModalShorteys()
        },
        onRegistryWizardHide() {
            this.setCreateCaseModalShorteys()
        },
        onRowDataChanged({ api }) {
            if (this.rowData.length === 1) {
                const singleRow = this.rowData[0]
                // TODO Check single row id prop
                const singleRowIdProp = 'patId'
                const singleRowId = singleRow.patId
                api.forEachNode(node => {
                    if (node.data[singleRowIdProp] === singleRowId) {
                        node.setSelected(true)
                    }
                })
            }
        },
        onRegistryWizardCreateCase() {
            this.$refs.registryWizard.hide()
            this.hide()
        },
        onCreateCaseInNewCaseRoute(patient) {
            this.loadAndSelectPatient(patient)
        },
        mapPatientFromHisPersonIdentifiers(personIdentifiers, centerIdentifier, centerPatientIdentifierType, centerIdentifierCenterName, centerIdentifierType) {
            let mappedIdentifiers = []
            Object.keys(personIdentifiers).forEach(identifier => {
                if (personIdentifiers[identifier].identifier) {
                    mappedIdentifiers.push({
                        paiIdentifier: personIdentifiers[identifier].identifier,
                        identifierType: {
                            name: personIdentifiers[identifier].identifierTypeName,
                            code: personIdentifiers[identifier].identifierTypeCode
                        }
                    })
                }
            })

            const mappedIdentifier =
                mappedIdentifiers.find(mappedIdentifier =>
                    mappedIdentifier.paiIdentifier === centerIdentifier &&
                    mappedIdentifier.identifierType?.code === centerPatientIdentifierType)
            if (!mappedIdentifier && centerIdentifier) {
                mappedIdentifiers.push({
                    centerPaiIdentifier: centerIdentifier,
                    identifierType: { name: centerIdentifierType, code: centerPatientIdentifierType },
                    center: { name: centerIdentifierCenterName }
                })
            }
            return mappedIdentifiers
        },
        mapPersonIdentifiers(personIdentifiers, centerIdentifier, centerPatientIdentifierType, centerIdentifierCenterCode) {
            let mappedIdentifiers = []
            Object.keys(personIdentifiers).forEach(identifier => {
                if (personIdentifiers[identifier].identifier) {
                    mappedIdentifiers.push({
                        identifier: personIdentifiers[identifier].identifier,
                        identifierType: identifier
                    })
                }
            })

            const mappedIdentifier =
                mappedIdentifiers.find(mappedIdentifier =>
                    mappedIdentifier.identifier === centerIdentifier &&
                        mappedIdentifier.identifierType === centerPatientIdentifierType)
            if (!mappedIdentifier && centerIdentifier) {
                mappedIdentifiers.push({
                    centerCode: centerIdentifierCenterCode,
                    identifier: centerIdentifier,
                    identifierType: centerPatientIdentifierType
                })
            }
            return mappedIdentifiers
        },
        mapFieldsToRequest(integrationPatient) {
            return {
                address: {
                    address: integrationPatient.address?.address,
                    postalCode: integrationPatient.address?.postalCode,
                    municipalityCode: integrationPatient.address?.municipality?.code ?? null,
                    provinceCode: integrationPatient.address?.province?.code ?? null,
                    countryCode: integrationPatient.address?.country?.code ?? null
                },
                birthDate: integrationPatient.patBirthdate,
                contactInformation: {
                    email: integrationPatient.contactInformation?.email,
                    mainPhone: integrationPatient.contactInformation?.mainPhone,
                    otherPhone1: integrationPatient.contactInformation?.otherPhone1,
                    otherPhone2: integrationPatient.contactInformation?.otherPhone2
                },
                gender: integrationPatient.genderCode,
                identifiers: this.mapPersonIdentifiers(integrationPatient.personIdentifiers, integrationPatient.centerIdentifier, integrationPatient.centerPatientIdentifierType, integrationPatient.centerIdentifierCenterCode),
                name: integrationPatient.patName,
                surname1: integrationPatient.patSurname1,
                surname2: integrationPatient.patSurname2
            }
        },
        async getPatientFromHis(hisPatient) {
            const integrationPatientRequest = this.mapFieldsToRequest(hisPatient)
            this.updateLoading(true)
            const [error, response] = await this.toCall(patientDiscrepancyServices.hisPatientWithDiscrepancy(this.laboratoryId, integrationPatientRequest))
            this.updateLoading(false)
            if (error) {
                this.showErrorModalGlobalFromErrorResponse(error)
                return false
            }
            return response
        },
        async patientWithOrWithoutDiscrepancy(hisPatient) {
            const integrationPatientRequest = this.mapFieldsToRequest(hisPatient)
            this.updateLoading(true)
            const [error, response] = await this.toCall(patientDiscrepancyServices.patientWithOrWithoutDiscrepancy(this.laboratoryId, integrationPatientRequest))
            this.updateLoading(false)
            if (error) {
                this.showErrorModalGlobalFromErrorResponse(error)
                return false
            }
            return response
        },
        async onPatientSelectionModalContinue(patient) {
            await this.searchPatients()
            this.showRegistryWizard(patient)
        },
        showRegistryWizard(patient) {
            this.cleanCreateCaseModalShorteys()
            this.$refs.registryWizard.show([patient.patId])
        },
        async onCreateCaseWithWizardClick() {
            const selectedPatient = this.selectedRows[0]
            if (!selectedPatient.patId) {
                const newLocalPatient = await this.patientWithOrWithoutDiscrepancy(selectedPatient)
                // patient returned from the above endpoint is the new localPatient
                if (newLocalPatient.withDiscrepancy) {
                    const patientFromHis = await this.getPatientFromHis(selectedPatient)
                    // if the new local patient have discrepancies show modal to resolve
                    this.$refs.patientSelectionModalWrapper.show([newLocalPatient, patientFromHis, null, null, null, this.laboratoryId])
                } else {
                    // if the new local patient doesn't have discrepancies, continue the process with that patient
                    this.showRegistryWizard(newLocalPatient)
                }
            } else {
                this.showRegistryWizard(selectedPatient)
            }
        },
        async loadAssociatedCase(casId) {
            this.updateLoading(true)
            const [err, response] = await this.toCall(caseServices.getAssociatedCases(this.$store.getters.labId, casId))
            this.updateLoading(false)
            if (err) {
                this.showErrorModalGlobal()
                return
            }
            return response
        },
        async showModalAssociatedCasesReadOnly(selectedCase) {
            this.selectedCaseCaseNumber = selectedCase.casenumber
            this.selectedCaseAssociatedCases = await this.loadAssociatedCase(selectedCase.id)
            this.$refs.modalAssociateCaseWrapper.show()
        },
        setDetailCellRendererParams() {
            this.detailCellRendererParams = {
                detailGridOptions: {
                    pagination: false,
                    rowModelType: AgGridRowModelType.CLIENT_SIDE,
                    floatingFilter: false,
                    sideBar: gridSideBarConfig,
                    getContextMenuItems: (params) => {
                        return [
                            {
                                name: this.$t('registry.btn.viewAnaReport'),
                                action: () => {
                                    const getCaseReportPDFParams = {
                                        laboratoryId: params?.node.data.laboratory.labId,
                                        caseId: params?.node.data.id,
                                        caseLabel: params?.node.data.casenumber,
                                        print: false
                                    }
                                    HelperUtils.getCaseReportPDF(getCaseReportPDFParams)
                                },
                                cssClasses: params?.node.data.status.code !== REQUEST_STATES.DRAFT.code ? '' : ['not-show']
                            }
                        ]
                    },
                    columnDefs: [
                        {
                            type: 'text',
                            field: 'casenumber',
                            headerName: this.$t('grid.casCasenumber')
                        },
                        {
                            type: AgGridColumnType.COMMON_CASE_STATUS,
                            field: 'status',
                            headerName: this.$tc('grid.detail.casStatusId')
                        },
                        {
                            type: 'associatedCases',
                            field: 'associatedCases',
                            headerName: this.$t('grid.detail.associatedCases'),
                            onCellClicked: params => {
                                this.showModalAssociatedCasesReadOnly(params?.node.data)
                            }
                        },
                        {
                            type: AgGridColumnType.DIACRITICS_INSENSITIVE_TEXT_FOR_CODE_DESCRIPTION_OR_STRING,
                            field: 'laboratory.labName',
                            headerName: this.$t('grid.detail.laboratory')
                        },
                        {
                            type: AgGridColumnType.DIACRITICS_INSENSITIVE_TEXT_FOR_CODE_DESCRIPTION_OR_STRING,
                            field: 'physicianDepartment.name',
                            headerName: this.$t('grid.detail.requestingUnit')
                        },
                        {
                            type: AgGridColumnType.DIACRITICS_INSENSITIVE_TEXT_FOR_CODE_DESCRIPTION_OR_STRING,
                            field: 'physicianPetitioner.fullName',
                            headerName: this.$t('grid.detail.requestingProfessional')
                        },
                        {
                            type: AgGridColumnType.DIACRITICS_INSENSITIVE_TEXT_FOR_CODE_DESCRIPTION_OR_STRING,
                            field: 'pathologist.fullName',
                            headerName: this.$t('grid.detail.patName')
                        },
                        {
                            type: AgGridColumnType.DIACRITICS_INSENSITIVE_TEXT_FOR_CODE_DESCRIPTION_OR_STRING,
                            field: 'notes',
                            headerName: this.$t('grid.detail.observations')
                        },
                        {
                            type: AgGridColumnType.DIACRITICS_INSENSITIVE_TEXT_FOR_CODE_DESCRIPTION_OR_STRING,
                            field: 'comprehensiveDiagnosis.text',
                            headerName: this.$t('grid.detail.comprehensiveDiagnosis'),
                            cellRendererFramework: CellGridRichText
                        },
                        {
                            type: 'text',
                            headerName: this.$t('registry.btn.consultation'),
                            headerTooltip: this.$t('registry.btn.consultation'),
                            colId: 'grid.caseOfReference',
                            field: 'grid.caseOfReference',
                            hide: false,
                            filter: false,
                            headerComponentParams: {
                                template: buildIconHeaderTemplate('far fa-glasses')
                            },
                            cellRenderer: (data) => {
                                if (data.value) {
                                    if (data.value === true) {
                                        return '<i class="success far fa-check" aria-hidden="true"/>'
                                    }
                                }
                                return ''
                            },
                            exportField: 'caseOfReference',
                            exportType: 'boolean'
                        }
                    ]
                },
                getDetailRowData: async params => {
                    if (params?.node.data.patId) {
                        const data = await this.getMasterDetailRowData(params?.node.data.patId)
                        params.successCallback(data)
                    } else {
                        params.successCallback([])
                    }
                }
            }
        },
        async getMasterDetailRowData(patId) {
            const [err, response] = await this.toCall(caseServices.getPreviousCases(this.laboratoryId, patId))
            if (err) {
                this.showErrorModalGlobal()
                return []
            }
            return response
        },
        onRowDoubleClicked({ data }) {
            this.loadAndSelectPatient(data)
        },
        focusIdentifier() {
            this.$nextTick(() => {
                this.$refs.patientIdentifier.focus()
            })
        },
        async addAttachment(file, errorMessage, addAttachmentFunction) {
            this.updateLoading(true)
            const [err] = await this.toCall(addAttachmentFunction(file))
            if (err) {
                if (err.response.data.code === apiExceptionType.MAX_UPLOAD_SIZE_EXCEEDED || err.response.status === apiExceptionType.ENTITY_TOO_LARGE) {
                    this.showErrorModalGlobal(this.$t('component.attachModal.error.maxFileSizeExceeded'))
                } else {
                    this.showErrorModalGlobal(this.$t(errorMessage))
                }
            } else {
                this.showToastSuccessGlobal()
            }
            await this.loadRowData()
            this.updateLoading(false)
        },
        openIncidentFormModal(patient) {
            const entities = {
                patId: patient.patId
            }
            this.$refs.incidenFormModal.show(null, entities)
        },
        openModalPatientFusion(firstPatient, secondPatient) {
            if (firstPatient && secondPatient) {
                this.$refs.modalPatientFusion.show([firstPatient.patId, secondPatient.patId])
            }
            this.$refs.modalPatientFusion.show()
        },
        openPatientPreviousCases(patient) {
            const routeData = this.$router.resolve({ name: 'patientCases', params: { patId: patient.patId, labId: this.$store.getters.labId } })
            window.open(routeData.href, '_blank')
        },
        showCreatePatientModal() {
            this.cleanCreateCaseModalShorteys()
            this.$refs.createPatientModal.show()
        },
        onSelectionChanged({ api }) {
            this.selectedRows = api.getSelectedRows()
        },
        getContextMenuItems(params) {
            if (this.selectedRows.length === 1) {
                return [
                    {
                        name: this.isReassign ? this.$t('registry.btn.reassignPatient') : this.$t('component.patient.searcher.createCase'),
                        action: () => this.selectPatientAlt(this.selectedRows[0])
                    },
                    {
                        name: this.$t('component.patient.searcher.patientInformation'),
                        action: () => this.openPatientInformation(this.selectedRows[0])
                    }
                ]
            } else {
                return []
            }
        },
        openPatientInformation(patient) {
            let patientFromHis = null
            if (patient.patId) {
                this.patientIdSelected = patient.patId
            } else {
                patientFromHis = this.mapPatientFromHisToModalPatientCardModel(patient)
            }
            this.$nextTick(() => {
                this.$refs.modalPatientCard.show(patientFromHis)
            })
        },
        mapPatientFromHisToModalPatientCardModel(patientFromHis) {
            const patientFromHisAddress = patientFromHis.address
            const patientFromHisContactInformation = patientFromHis.contactInformation
            return {
                address: {
                    address: patientFromHisAddress?.address,
                    postalCode: patientFromHisAddress?.postalCode,
                    municipality: { name: patientFromHisAddress?.municipality?.name },
                    province: { name: patientFromHisAddress?.province?.name },
                    country: { name: patientFromHisAddress?.country?.name }
                },
                patBirthdate: patientFromHis.patBirthdate,
                contactInformation: {
                    email: patientFromHisContactInformation?.email,
                    mainPhone: patientFromHisContactInformation?.mainPhone,
                    otherPhone1: patientFromHisContactInformation?.otherPhone1,
                    otherPhone2: patientFromHisContactInformation?.otherPhone2
                },
                gender: { name: patientFromHis.genderName },
                patientIdentifiers: this.mapPatientFromHisPersonIdentifiers(
                    patientFromHis.personIdentifiers,
                    patientFromHis.centerIdentifier,
                    patientFromHis.centerPatientIdentifierType,
                    patientFromHis.centerIdentifierCenterName,
                    patientFromHis.centerIdentifierType),
                patName: patientFromHis.patName,
                patSurname1: patientFromHis.patSurname1,
                patSurname2: patientFromHis.patSurname2,
                patMiddleName: patientFromHis.patMiddleName
            }
        },
        showEditPatientModal(patId) {
            this.$refs.editPatientModal.show(patId)
        },
        async loadAndSelectPatient(patient) {
            if (patient.patId) {
                const patientDetail = await this.loadPatient(patient.patId)
                if (patientDetail) {
                    this.$emit('selected', patientDetail)
                    this.hide()
                }
            } else {
                this.$emit('selectedPatientFromHis', patient)
            }
        },
        async selectPatientAlt(patient) {
            if (this.isReassign) {
                const confirmationModalPromise = await this.$_showConfirmationModal()
                if (confirmationModalPromise) {
                    await this.loadAndSelectPatient(patient)
                }
            } else {
                await this.loadAndSelectPatient(patient)
            }
        },
        async loadPatient(patId) {
            this.updateLoading(true)
            const [err, response] = await this.toCall(patientResource.findById(patId))
            this.updateLoading(false)
            if (err) {
                this.showErrorModalGlobal()
                return false
            }
            return response
        },
        onGridReady({ api }) {
            this.gridApi = api
            this.gridApi.setColumnDefs(
                this.$_filterPatientColumns(
                    this.columnDefs,
                    'patMiddleName',
                    'patSurname2'
                )
            )
        },
        hide() {
            this.$refs.bModal.hide()
        },
        async show(isReassign = false) {
            this.setCreateCaseModalShorteys()
            this.setDetailCellRendererParams()
            this.isReassign = isReassign
            await Promise.all([
                this.addPatientIdentifiersColumns(),
                this.getIdentifiers(),
                this.getCenters()
            ])
            this.isModalOpen = true
            this.$refs.bModal.show()
        },
        async getIdentifiers() {
            const [err, response] = await this.toCall(PatientIdentifierResource.index())
            if (err) {
                this.showErrorModalGlobal()
                return
            }
            this.identifiers = response
            this.identifierSelected = this.globalParameters.mainPatientIdentifierType
        },
        async getCenters() {
            const [err, response] = await this.toCall(CenterResource.getActiveCenters())
            if (err) {
                this.showErrorModalGlobal()
                return
            }
            this.centers = response
            if (this.sessionStoreState.selectedLaboratoryConfiguration?.center) {
                this.centerSelected = this.sessionStoreState.selectedLaboratoryConfiguration.center
            }
        },
        onHide() {
            patientResource.cancelAxiosCall()
            this.isModalOpen = false
            this.$_cleanComponentData()
            this.$emit('onHide')
        },
        onOk() {
        },
        validatePatientId(id) {
            if (id) {
                for (let regex of this.regex) {
                    let regexObj = new RegExp('^(?:' + regex.regex + ')$')
                    if (regexObj.test(id.trim())) {
                        return true
                    }
                }
            }
            return false
        },
        async searchPatients() {
            const validId = this.validatePatientId(this.id)
            if (!validId && this.id && this.id.length) {
                this.showErrorModalGlobal(this.$t('component.modalWarning.content'))
            } else if (this.name || this.surname1 || this.surname2 || this.middleName || validId) {
                this.noSearchDoneYet = false
                this.loading = true
                const [error] = await this.toCall(this.loadRowData())
                this.loading = false
                this.searchDone = true
                if (error) {
                    this.handleError(error)
                }
            }
            this.selectedRows = []
        },
        clearSearch() {
            this.identifierSelected = this.globalParameters.mainPatientIdentifierType
            this.centerSelected = StoreUtils.selectedLaboratoryConfiguration?.center
            this.id = ''
            this.name = ''
            this.surname1 = ''
            this.surname2 = ''
            this.middleName = ''
            this.searchDone = false
        },
        async loadRowData() {
            const paginator = await this.loadPatientPaginator()
            if (paginator.totalElements === 0) {
                this.tooManyResults = false
                this.rowData = []
            } else {
                this.rowData = paginator.content
                this.tooManyResults = false
            }
        },
        async loadPatientPaginator() {
            let params = {
                size: PAGINATOR_BIG_PAGE_SIZE,
                id: this.id,
                surname1: this.surname1,
                surname2: this.surname2,
                requestingLaboratoryId: this.$store.getters.labId,
                middleName: this.middleName,
                name: this.name,
                identifierType: this.identifierSelected.code
            }
            if (this.identifierSelected.code === this.centerIdentifierCode) {
                params.centerCode = this.centerSelected.code
            }
            return patientResource.paginatorMultiSourced(params)
        },
        handleError(e) {
            if (!e.response) {
                throw e
            }
            if (e.response.data.code === this.$apiExceptions.RESULT_LIMIT_EXCEEDED) {
                this.rowData = []
                this.tooManyResults = true
            }
        },
        async selectPatient(selectedPatient) {
            this.selectedPatient = await patientResource.findById(
                selectedPatient.patId
            )
        },
        async exportPatientPreviousCasesReportPDF(patient) {
            const blob =
                await this.$_toCallUnique(
                    patientResource.getPreviousCaseAPReport(this.$store.getters.labId, patient.patId, false))
            if (blob) {
                const openFileParams = {
                    blob,
                    newWindowTabTitle: `- ${this.$tc('global.caseReport')}` // TODO: Falta mainPatientIdentifier en DTO
                }
                this.$_openFile(openFileParams)
            }
        },
        async addPatientIdentifiersColumns() {
            const [err, response] = await this.toCall(PatientIdentifierResource.getPersonIdentifiers())
            if (err) {
                this.showErrorModalGlobal()
                return
            }
            const personIdentifiers = response
            if (personIdentifiers.length) {
                personIdentifiers.forEach((identifier) => {
                    this.columnDefs.push({
                        type: 'text',
                        field: identifier.code,
                        colId: identifier.code,
                        headerName: identifier.name,
                        valueGetter: (params) => {
                            return params.data.personIdentifiers[identifier.code.toString()]?.identifier
                        }
                    })
                })
            }
        }
    }
}
</script>
<style lang="scss">
.create-case-modal {
    .loading-state {
        width: 100%;
        height: calc(100vh - 400px);
        display: flex;
        align-items: center;
        justify-content: center;
    }
}
.create-case-modal .id-model-select .multiselect__content-wrapper {
    width: auto;
    min-width: 100px;
}
.create-case-modal .center-selection .multiselect__content-wrapper {
    width: auto;
    min-width: 230px;
}
.create-case-modal .ag-layout-normal {
    padding: 0;
}
.create-case-modal .patient-container .empty-search {
    height: calc(100vh - 400px);
    min-height: 350px;
}
.create-case-modal .patient-container .btn__icon .btn__icon--primary {
    margin: 0 3px !important;
}
.create-case-modal .form-control {
    color: vt-color(primary) !important;
}
.create-case-modal .registry-case-wizard .form-control {
    color: vt-color(dark-text) !important;
}
.create-case-modal .search-box input,
.patientFusionModal .search-box input {
    border: none;
    border-radius: 0;
}
.create-case-modal .input-section,
.patientFusionModal .input-section {
    position: relative;
    width: 100%;
    color: vt-color(primary);
}
.create-case-modal .input-section:before,
.patientFusionModal .input-section:before {
    content: '';
    width: 1px;
    height: 15px;
    background: vt-color(general-border);
    z-index: 1;
    position: absolute;
    top: 0;
    bottom: 0;
    right: 0;
    margin: auto;
}
.create-case-modal .search-box div div:nth-last-child(2) .input-section:before,
.patientFusionModal .search-box div div:nth-last-child(2) .input-section:before {
    background: transparent;
}
.create-case-modal .search-box .col,
.patientFusionModal .search-box .col {
    display: flex;
    align-items: center;
}
.create-case-modal .select-section,
.patientFusionModal .select-section {
    position: relative;
    width: 100%;
}
.create-case-modal .select-section .multiselect__tags,
.patientFusionModal .select-section .multiselect__tags {
    border: none;
    display: flex;
    align-items: center;
}
.create-case-modal .select-section .multiselect__tags input,
.patientFusionModal .select-section .multiselect__tags input {
    margin-bottom: 0 !important;
}
.create-case-modal .select-section .multiselect__select,
.patientFusionModal .select-section .multiselect__select {
    padding: 0;
    top: -2px;
}
.create-case-modal .multiselect__placeholder,
.patientFusionModal .multiselect__placeholder {
    color: #495057;
}
.create-case-modal .select-section.identifiers .multiselect__placeholder,
.patientFusionModal .select-section.identifiers .multiselect__placeholder {
    color: vt-color(primary);
}
.create-case-modal .multiselect,
.patientFusionModal .multiselect {
    color: vt-color(primary);
}
.create-case-modal .registry-case-wizard .multiselect {
    color: vt-color(dark-text);
}
.create-case-modal .icon-column {
    width: 70px;
}
.create-case-modal .modal-body {
    border-radius: .3rem;
    min-height: calc(100vh - 160px);
}
.create-case-modal .create-patient-modal .modal-body,
.create-case-modal .edit-patient-modal .modal-body {
    height: auto !important;
}
.create-case-modal .patientFusionModal .modal-body {
    height: calc(100vh - 240px);
}
.create-case-modal .create-patient-modal {
    padding: 0 !important;
}
.create-case-modal .vt-tabs .nav-tabs .nav-link,
.vt-tabs--small .nav-tabs .nav-link {
    font-size: font-size(very-small) !important;
}
.create-case-modal .nav-pills {
    padding: 0 !important;
    background: vt-color(light-primary);
    height: 100%;
}
.create-case-modal .tabs.row {
    height: calc(100vh - 360px) !important;
    border: 1px solid rgba(0, 0, 0, 0.125);
}
.create-case-modal .nav-link.active {
    background: vt-color(white-light);
    color: vt-color(primary);
    position: relative;
}
.create-case-modal .nav-link.active::before {
    content: "";
    position: absolute;
    width: 5px;
    height: 100%;
    background: vt-color(primary);
    left: 0;
}
.create-case-modal .nav-link {
    border-radius: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    height: 40px;
    width: 70px;
    border-bottom: 2px solid vt-color(white-light);
}
.create-case-modal .vt-tabs .nav-tabs .nav-link.active,
.vt-tabs--small .nav-tabs .nav-link.active {
    border-bottom: 2px solid vt-color(primary);
}
.create-case-modal .vt-tabs .nav-tabs .nav-link,
.vt-tabs--small .nav-tabs .nav-link {
    border-bottom: 2px solid transparent;
}
.create-case-modal .vt-tabs .nav-tabs .nav-link,
.vt-tabs--small .nav-tabs .nav-link i {
    font-size: font-size(very-normal) !important;
}
.create-case-modal .techniques-tab .nav-link.active::before {
    background: transparent;
}
.create-case-modal .section-indicator {
    position: absolute;
    width: auto;
    left: 23px;
    margin-top: 5px;
}
.create-case-modal .nav-link {
    width: 50px;
}
</style>
<style lang="scss"
       scoped>
.title {
    font-size: font-size(normal);
    color: vt-color(primary);
}
.vertical-line {
    width: 1px;
    background: #dee2e6;
    display: inline-block;
    margin: 0 15px;
    height: 20px;
}
.ps-filter-form {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    max-width: 1200px;
    margin-bottom: 24px;
}
.ps-grid {
    height: 450px;
}
.create-case-modal .ag-status-bar {
    display: none;
}
small {
    color: grey;
}
.error-msg {
    font-size: 1.1rem;
    padding-top: 1rem;
    color: #fc6274;
}
</style>
<style lang="scss">
.create-case-modal .form-group {
    margin-bottom: 0;
}
.create-case-modal .page_renderer {
    padding: 0 5px !important;
    height: calc(100% - 30px) !important;
    overflow-y: auto !important;
    margin: 10px !important;
}
.create-case-modal .page_renderer .vt-grid.ag-theme-balham.ps-grid {
    height: auto !important;
}
.create-case-modal .ps-grid {
    height: calc(100vh - 290px) !important;
    min-height: 425px;
}
.ps-grid.patient .ag-row.ag-row-no-focus.ag-row-odd.ag-row-level-1.ag-full-width-row.ag-row-position-absolute {
    background-color: #FFF !important;
}

</style>
